import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, ChevronRight, X, ShieldCheck } from 'lucide-react';
import { Button } from './Button';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const menuVariants = {
  closed: { 
    opacity: 0,
    y: "-100%",
    transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] }
  },
  open: { 
    opacity: 1,
    y: "0%",
    transition: { duration: 0.4, ease: [0.32, 0.72, 0, 1] }
  }
};

const itemVariants = {
  closed: { opacity: 0, y: 20 },
  open: (i: number) => ({ 
    opacity: 1, 
    y: 0, 
    transition: { delay: 0.1 + (i * 0.05), duration: 0.4 } 
  })
};

export const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose }) => {
  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const handleLinkClick = (id: string) => {
    onClose();
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 400); // Wait for menu close animation
  };

  return (
    <motion.div
      initial="closed"
      animate={isOpen ? "open" : "closed"}
      variants={menuVariants}
      className="fixed inset-0 z-40 bg-slate-50/95 backdrop-blur-xl flex flex-col pt-24 pb-8 px-6 md:hidden"
    >
      {/* Decorative background blob */}
      <div className="absolute top-[-10%] right-[-10%] w-[300px] h-[300px] bg-brand-100/40 rounded-full blur-[60px] -z-10 pointer-events-none" />

      <div className="flex-1 flex flex-col justify-between">
        
        {/* Navigation Links */}
        <nav className="flex flex-col gap-6 mt-4">
          <motion.button 
            custom={0} variants={itemVariants}
            onClick={() => handleLinkClick('schadecheck')}
            className="text-3xl font-semibold text-slate-900 text-left flex items-center justify-between group"
          >
            Schadecheck
            <ChevronRight className="text-brand-200 group-active:text-brand-600 transition-colors" size={24} />
          </motion.button>
          
          <motion.button 
            custom={1} variants={itemVariants}
            onClick={() => handleLinkClick('contact')}
            className="text-3xl font-semibold text-slate-900 text-left flex items-center justify-between group"
          >
            Werkwijze
            <ChevronRight className="text-brand-200 group-active:text-brand-600 transition-colors" size={24} />
          </motion.button>

          <motion.button 
            custom={2} variants={itemVariants}
            onClick={() => handleLinkClick('contact')}
            className="text-3xl font-semibold text-slate-900 text-left flex items-center justify-between group"
          >
            Over ons
            <ChevronRight className="text-brand-200 group-active:text-brand-600 transition-colors" size={24} />
          </motion.button>
        </nav>

        {/* Action Area */}
        <div className="space-y-6">
          <motion.div custom={3} variants={itemVariants} className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
            <p className="text-sm text-slate-500 font-medium mb-3 uppercase tracking-wider">Direct hulp nodig?</p>
            <a href="tel:0201234567" className="flex items-center gap-4 text-slate-900 group">
              <div className="w-10 h-10 bg-brand-50 rounded-full flex items-center justify-center text-brand-600 group-active:bg-brand-600 group-active:text-white transition-colors">
                <Phone size={20} />
              </div>
              <span className="text-2xl font-bold">020 - 123 45 67</span>
            </a>
          </motion.div>

          <motion.div custom={4} variants={itemVariants}>
            <Button 
              fullWidth 
              size="lg" 
              onClick={() => handleLinkClick('contact')}
              className="text-lg shadow-xl shadow-brand-900/10"
            >
              Gratis intake aanvragen
            </Button>
          </motion.div>
          
          <motion.div custom={5} variants={itemVariants} className="pt-4 border-t border-slate-200">
             <div className="flex items-center justify-center gap-2 text-slate-400 text-xs font-medium">
                <ShieldCheck size={14} />
                <span>NIVRE</span>
                <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                <span>LSA</span>
                <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                <span>Keurmerk Letselschade</span>
             </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};